export type AssistantActionKind = 'approve_holiday' | 'create_shift' | 'send_message';

export interface PendingAction {
  kind: AssistantActionKind;
  title: string;
  summary: string;
  details: { label: string; value: string }[];
}

export interface AssistantTurn {
  text: string;
  speak: string;
  pendingAction?: PendingAction;
}

function includesAny(text: string, words: string[]) {
  return words.some((w) => text.includes(w));
}

/** Local mock — no API. Used to test voice UX and confirmation cards. */
export function mockStafflyAssistant(userText: string): AssistantTurn {
  const t = userText.trim().toLowerCase();

  if (t.includes('approve') && (t.includes('holiday') || t.includes('leave') || t.includes('jane'))) {
    return {
      text: 'I found a pending holiday request. Confirm below to approve it.',
      speak: 'I found a pending holiday for Jane Smith. Please confirm on screen.',
      pendingAction: {
        kind: 'approve_holiday',
        title: 'Approve holiday?',
        summary: 'This will notify Jane and update leave balance.',
        details: [
          { label: 'Employee', value: 'Jane Smith' },
          { label: 'Dates', value: '20–24 Aug 2026' },
          { label: 'Days', value: '5 days (Annual leave)' },
          { label: 'Status', value: 'Pending' },
        ],
      },
    };
  }

  if (includesAny(t, ['create', 'add', 'put']) && includesAny(t, ['shift', 'rota'])) {
    return {
      text: 'I can create this shift. Confirm the details first.',
      speak: 'Please confirm the new shift on screen.',
      pendingAction: {
        kind: 'create_shift',
        title: 'Create shift?',
        summary: 'Overlap checks run only after you confirm.',
        details: [
          { label: 'Employee', value: 'Alex Young' },
          { label: 'When', value: 'Tomorrow 07:00–15:00' },
          { label: 'Location', value: 'Main Site' },
          { label: 'Role', value: 'Healthcare Assistant' },
        ],
      },
    };
  }

  if (includesAny(t, ['message', 'text', 'tell', 'send']) && includesAny(t, ['message', 'text', 'tell', 'send'])) {
    return {
      text: 'I can send this message. Confirm before it goes out.',
      speak: 'Please confirm the message on screen.',
      pendingAction: {
        kind: 'send_message',
        title: 'Send message?',
        summary: 'Will send as you, from this Staffly account.',
        details: [
          { label: 'To', value: 'Site supervisors' },
          { label: 'Message', value: 'Please check tonight’s rota coverage.' },
        ],
      },
    };
  }

  if (includesAny(t, ['absent', 'absentee', 'who is off', "didn't clock", 'not clocked'])) {
    return {
      text: '7 people are absent today: Priya Shah, Tom Reed, Aisha Khan, Liam Cole, Nora Patel, Ben Walsh, and Mei Chen.',
      speak: 'Seven people are absent today. The full list is on screen.',
    };
  }

  if (includesAny(t, ['visa', 'permit', 'expir'])) {
    return {
      text: '3 visas expire in the next 90 days: Kevin Allen (12 Sep), Paul Hall (28 Sep), and Alexis Young (3 Oct).',
      speak: 'Three visas expire in the next 90 days. Details are on screen.',
    };
  }

  if (includesAny(t, ['late', 'tardy'])) {
    return {
      text: '2 late clock-ins yesterday: Sam Ortiz (18 minutes) and Riley Ng (41 minutes).',
      speak: 'Two people were late yesterday. Names are on screen.',
    };
  }

  if (includesAny(t, ['shift', 'early morning', 'afternoon', 'evening', 'how many'])) {
    return {
      text: 'Today there are 12 early morning, 19 afternoon, and 8 evening shifts.',
      speak: 'Twelve early morning, nineteen afternoon, and eight evening shifts today.',
    };
  }

  return {
    text: `Sample assistant only — no live data. Try: “Who is absent today?”, “Whose visa is expiring?”, or “Approve Jane’s holiday.”`,
    speak: 'This is a sample. Try asking who is absent today, or approve Jane’s holiday.',
  };
}

export function mockActionResult(kind: AssistantActionKind, approved: boolean): AssistantTurn {
  if (!approved) {
    return { text: 'Cancelled. Nothing was changed.', speak: 'Cancelled.' };
  }
  if (kind === 'approve_holiday') {
    return { text: 'Done. Jane Smith’s holiday is approved and she has been notified.', speak: 'Done. Jane has been notified.' };
  }
  if (kind === 'create_shift') {
    return { text: 'Done. Shift created for Alex Young tomorrow 07:00–15:00 at Main Site.', speak: 'Done. The shift has been created.' };
  }
  return { text: 'Done. Message sent to site supervisors.', speak: 'Done. The message has been sent.' };
}
